import {
  useState,
  useRef,
  useEffect,
  ReactNode,
  Fragment,
  CSSProperties,
} from 'react';

import { Box, SpacingValue } from '../Box';
import { Link } from '../Link';
import { LinkColor } from '../Link/Link.types';
import { Typography, TypographyColor, TypographyVariant } from '../Typography';

/**
 * Свойства компонента {@link TruncatedText}
 */
interface TruncatedTextProps {
  /**
   * Текст, который будет отображаться и обрезаться.
   */
  text: string;

  /**
   * Вариант текста для Typography.
   */
  textVariant?: TypographyVariant;

  /**
   * Максимальное число строк для отображения перед обрезкой текста.
   *
   * @defaultValue `10`
   */
  rows?: number;

  alwaysShowButton?: boolean;
  children?: ReactNode | ReactNode[];

  /**
   * Цвет текста.
   *
   * @defaultValue `'text.light.primary'`
   */
  color?: TypographyColor;
  buttonVariant?: keyof typeof LinkColor;
  buttonPadding?: SpacingValue;
  wordBreak?: CSSProperties['wordBreak'];
}

/**
 * Компонент, который обрезает текст до указанного числа строк и предоставляет переключатель для показа или скрытия полного текста.
 *
 * @public
 * @param text - Текст, который будет отображаться и обрезаться.
 * @param rows - Максимальное число строк для отображения перед обрезкой текста.
 * @param color - Цвет текста.
 * @param textVariant - Вариант текста для Typography.
 * @param alwaysShowButton - Показывать переключатель даже когда текст помещается целиком.
 * @param children - Содержимое, отображаемое под текстом в развёрнутом состоянии.
 * @param buttonVariant - Цвет переключателя.
 * @param buttonPadding - Отступ сверху у переключателя.
 * @param wordBreak - Правило переноса слов в тексте.
 * @returns Компонент TruncatedText.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link TruncatedTextProps}
 */
export const TruncatedText = ({
  text,
  alwaysShowButton = false,
  children,
  rows = 10,
  textVariant = 'body2',
  color = 'text.light.primary',
  buttonVariant,
  buttonPadding = '8',
  wordBreak,
}: TruncatedTextProps) => {
  const rowHeight = 24;
  const maxHeight = rowHeight * rows;
  const [isTruncated, setIsTruncated] = useState(true);
  const [textHeight, setTextHeight] = useState(0);
  const textRef = useRef<HTMLDivElement>(null);
  const showButton = alwaysShowButton || textHeight > maxHeight;

  useEffect(() => {
    if (textRef.current) {
      setTextHeight(textRef.current.clientHeight);
    }
  }, []);

  const toggleVisibility = () => setIsTruncated((prev) => !prev);

  return (
    <>
      {text && (
        <Box
          overflow={isTruncated ? 'hidden' : 'visible'}
          maxHeight={isTruncated ? maxHeight : 'none'}
          testId="truncated-text-test-id"
        >
          <div ref={textRef}>
            <Typography
              variant={textVariant}
              color={color}
              wordBreak={wordBreak}
            >
              {text.split('\n').map((paragraph: string) => (
                <Fragment key={paragraph}>
                  {paragraph}
                  <br />
                </Fragment>
              ))}
            </Typography>
          </div>
        </Box>
      )}

      {!isTruncated && children}

      {showButton && (
        <Box pt={buttonPadding}>
          <Link
            stroke={false}
            onClick={toggleVisibility}
            variant={buttonVariant}
          >
            {isTruncated ? 'Показать еще' : 'Скрыть'}
          </Link>
        </Box>
      )}
    </>
  );
};
